import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { CardAction, Section } from '@omni/ai/features/card.schema';
import { CardTextComponent } from './sections/card-text.component';
import { CardInsightComponent } from './sections/card-insight.component';
import { CardMetricComponent } from './sections/card-metric.component';
import { CardStatsComponent } from './sections/card-stats.component';
import { CardChartComponent } from './sections/card-chart.component';
import { CardProgressComponent } from './sections/card-progress.component';
import { CardListComponent } from './sections/card-list.component';
import { CardFactsComponent } from './sections/card-facts.component';
import { CardEntitiesComponent } from './sections/card-entities.component';
import { CardTimelineComponent } from './sections/card-timeline.component';

/** Section types that should span the full card body width (never share a grid row). */
const SPAN_TYPES: ReadonlySet<Section['type']> = new Set([
  'text',
  'callout',
  'insight',
  'stats',
  'chart',
  'progress',
  'list',
  'facts',
  'entities',
  'timeline',
]);

/**
 * Section dispatcher (Template Method): switches on `section.type` and renders
 * the matching leaf. Each branch narrows the discriminated union so leaf inputs
 * are fully typed — no `any`. Wide types get the `.nova-section--span` host class
 * so the body grid lets only `metric` sections flow side-by-side.
 */
@Component({
  selector: 'nova-section',
  standalone: true,
  imports: [
    CommonModule,
    CardTextComponent,
    CardInsightComponent,
    CardMetricComponent,
    CardStatsComponent,
    CardChartComponent,
    CardProgressComponent,
    CardListComponent,
    CardFactsComponent,
    CardEntitiesComponent,
    CardTimelineComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nova-section.component.html',
  host: { '[class.nova-section--span]': 'isSpan' },
})
export class NovaSectionComponent {
  @Input({ required: true }) section!: Section;

  /** Re-emitted from `<card-entities>` — unchanged `{kind,id}` contract. */
  @Output() openEntity = new EventEmitter<{ kind: 'contact' | 'account'; id: string }>();

  /** Re-emitted from per-insight action chips. */
  @Output() action = new EventEmitter<CardAction>();

  get isSpan(): boolean {
    return SPAN_TYPES.has(this.section.type);
  }

  // Typed narrowing accessors keep the template free of `$any` casts. The
  // dispatcher only reads these inside the matching *ngSwitchCase branch.
  asText(s: Section): Extract<Section, { type: 'text' }> {
    return s as Extract<Section, { type: 'text' }>;
  }
  asCallout(s: Section): Extract<Section, { type: 'callout' }> {
    return s as Extract<Section, { type: 'callout' }>;
  }
  asInsight(s: Section): Extract<Section, { type: 'insight' }> {
    return s as Extract<Section, { type: 'insight' }>;
  }
  asMetric(s: Section): Extract<Section, { type: 'metric' }> {
    return s as Extract<Section, { type: 'metric' }>;
  }
  asStats(s: Section): Extract<Section, { type: 'stats' }> {
    return s as Extract<Section, { type: 'stats' }>;
  }
  asChart(s: Section): Extract<Section, { type: 'chart' }> {
    return s as Extract<Section, { type: 'chart' }>;
  }
  asProgress(s: Section): Extract<Section, { type: 'progress' }> {
    return s as Extract<Section, { type: 'progress' }>;
  }
  asList(s: Section): Extract<Section, { type: 'list' }> {
    return s as Extract<Section, { type: 'list' }>;
  }
  asFacts(s: Section): Extract<Section, { type: 'facts' }> {
    return s as Extract<Section, { type: 'facts' }>;
  }
  asEntities(s: Section): Extract<Section, { type: 'entities' }> {
    return s as Extract<Section, { type: 'entities' }>;
  }
  asTimeline(s: Section): Extract<Section, { type: 'timeline' }> {
    return s as Extract<Section, { type: 'timeline' }>;
  }
}
